import { useRef, useState } from "react";
import { useColorModeValue } from "@chakra-ui/react";
import { t } from "@/utils";
import { Button, Flex, Heading } from "@/ui";
import FadeToggle from "@/components/FadeToggle";
import Lottie, { DotLottie } from "@/components/Lottie";
import Announcer from "@/modules/Announcer";
import { useDisableNavigationEvents } from "@/hooks";
import { updateCameraStationSettings } from "@/modules/cloud/station";
import { useDevices } from "@/store/devices";
import { useMount } from "@/hooks/useLifeCycles";
import LoadingSteps from "./LoadingSteps";
import type { WizardProps } from "../../Wizard";
import customizeAnimationLight from "assets/animations/customize_light.json?url";
import customizeAnimationDark from "assets/animations/customize_dark.json?url";

export default function Customizing({
  state,
  wizard
}: WizardProps<{
  cameraName: string;
  cameraColor: CameraColorId;
  cameraIcon: CameraIconId;
  cameraUseCases: string[];
  jid: string;
}>) {
  const [isSequenceComplete, setIsSequenceComplete] = useState(false);
  const animationRef = useRef<DotLottie | null>(null);
  const customizeAnimation = useColorModeValue(customizeAnimationLight, customizeAnimationDark);
  const updatePairedDevice = useDevices((store) => store.updatePairedDevice);
  useDisableNavigationEvents(false);

  useMount(async () => {
    log.info("Customizing camera", state);
    try {
      const { jid, cameraName, cameraColor, cameraIcon, cameraUseCases } = state;
      const response = await updateCameraStationSettings({
        xmppLogin: jid,
        name: cameraName,
        color: cameraColor,
        icon: cameraIcon,
        useCases: JSON.stringify(cameraUseCases)
      });
      if (response) {
        Announcer.getInstance().announceItemChange("DEVICES");
        updatePairedDevice({
          jid: jid,
          name: cameraName,
          color: cameraColor,
          icon: cameraIcon
        });
      }
    } catch (e) {
      log.err("Failed to customize camera", e);
    }
  });

  return (
    <Flex grow={1} direction="column" alignItems="center" w="100%">
      <Flex grow={1} direction="column" alignItems="center" justifyContent="center" w="100%">
        <Lottie
          src={customizeAnimation}
          loop={false}
          props={{ height: "192px" }}
          events={[
            [
              "complete",
              () => {
                animationRef.current?.setFrame(360);
                animationRef.current?.play();
              }
            ]
          ]}
          getDotLottie={(dotLottie) => {
            animationRef.current = dotLottie;
          }}
        />
        <FadeToggle component={Heading} in={isSequenceComplete} props={{ mt: "24", as: "h4", textAlign: "center" }}>
          {t("settingUp")}
          {t("customized")}
        </FadeToggle>
        <LoadingSteps onComplete={() => setIsSequenceComplete(true)} />
      </Flex>
      <Button
        transition="all 0.3s"
        opacity={isSequenceComplete ? 1 : 0}
        pointerEvents={isSequenceComplete ? "auto" : "none"}
        bg="primary"
        color="on-primary"
        minW="18.5rem"
        onClick={() => wizard.close()}
      >
        {t("buttons.continue")}
      </Button>
    </Flex>
  );
}
